import { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API, EmptyBox, ErrorBox, LoadingBox, TabHeader, asList, fmtCell, labelFor } from './AdminShared';

const SKIP_KEYS = new Set(['request_id', 'id']);

export const AdminVerifications = ({ authHeaders, tx }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API}/api/admin/app-verifications?status=pending`, { headers: authHeaders() });
      if (!res.ok) throw new Error('verifications');
      setItems(asList(await res.json(), ['requests', 'items', 'verifications']));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (item, action) => {
    const id = item.request_id || item.id;
    const note = (notes[id] || '').trim();
    const name = item.display_name || item.user_name || item.email || id;
    if (action === 'reject' && note.length < 3) {
      toast.error(tx.verifications.noteRequired);
      return;
    }
    const confirmText = action === 'approve' ? tx.verifications.approveConfirm : tx.verifications.rejectConfirm;
    if (!window.confirm(`${confirmText} ${name}?`)) return;
    setBusy(id);
    try {
      const res = await fetch(`${API}/api/admin/app-verifications/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ note }),
      });
      if (!res.ok) throw new Error('decide');
      toast.success(tx.verifications.done);
      setItems((prev) => prev.filter((x) => (x.request_id || x.id) !== id));
    } catch {
      toast.error(tx.verifications.fail);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section data-testid="app-verifications-section">
      <TabHeader
        icon={BadgeCheck}
        title={tx.verifications.title}
        count={items.length}
        onRefresh={load}
        refreshLabel={tx.common.refresh}
        testid="app-verifications"
      />

      <div className="mt-6 space-y-4">
        {loading && <LoadingBox text={tx.common.loading} testid="app-verifications-loading" />}
        {!loading && error && (
          <ErrorBox text={tx.common.unavailable} retryText={tx.common.retry} onRetry={load} testid="app-verifications-error" />
        )}
        {!loading && !error && items.length === 0 && (
          <EmptyBox text={tx.verifications.empty} testid="app-verifications-empty" />
        )}
        {!loading &&
          !error &&
          items.map((item) => {
            const id = item.request_id || item.id;
            const entries = Object.entries(item).filter(([k]) => !SKIP_KEYS.has(k));
            return (
              <article key={id} data-testid={`verification-${id}`} className="rounded-2xl glass-dark p-6">
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                  {entries.map(([k, v]) => (
                    <li key={k} className="flex items-baseline justify-between gap-3 text-sm border-b border-white/5 pb-2">
                      <span className="text-slate-500 text-xs uppercase tracking-wide">{labelFor(k, tx.fields)}</span>
                      <span className="text-slate-200 text-right">{fmtCell(k, v)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex flex-wrap items-end gap-3">
                  <textarea
                    rows={2}
                    data-testid={`verification-note-${id}`}
                    placeholder={tx.verifications.notePlaceholder}
                    value={notes[id] || ''}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [id]: e.target.value }))}
                    className="flex-1 min-w-[240px] rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-krug-tealbright resize-none transition-colors duration-300"
                  />
                  <button
                    type="button"
                    data-testid={`verification-approve-${id}`}
                    disabled={busy === id}
                    onClick={() => decide(item, 'approve')}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-5 py-2.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-50 transition-colors duration-300"
                  >
                    <CheckCircle2 size={14} />
                    {tx.verifications.approve}
                  </button>
                  <button
                    type="button"
                    data-testid={`verification-reject-${id}`}
                    disabled={busy === id}
                    onClick={() => decide(item, 'reject')}
                    className="inline-flex items-center gap-2 rounded-full bg-red-500/15 border border-red-400/40 px-5 py-2.5 text-xs font-bold text-red-400 hover:bg-red-500/25 disabled:opacity-50 transition-colors duration-300"
                  >
                    <XCircle size={14} />
                    {tx.verifications.reject}
                  </button>
                </div>
              </article>
            );
          })}
      </div>
    </section>
  );
};

export default AdminVerifications;
